// covered-by: frontends/carrie/src/routes/roi/routes/audit-log/__tests__/view.test.tsx
import { type GroupedAuditLogEntry } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  CaretDown,
  CaretRight,
  PlusCircle,
} from "@phosphor-icons/react";
import {
  Button,
  DropdownMenu,
  Flex,
  Heading,
  Link as RadixLink,
  Select,
  Spinner,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { auditLogRoute } from "./route";

import { useGroupedAuditLog } from "../../../../utils/queries/auditLog";
import { roiRoute } from "../../route";

// All task categories in order (AT1-AT10)
// Source of truth: services/samba/temporal/activities/review_accuracy/models.py
const ALL_TASK_CATEGORIES = [
  { id: "AT1", label: "Previous Feedback Implemented" },
  { id: "AT2", label: "Required Legal/Regulatory Text" },
  { id: "AT3", label: "Copy Errors" },
  { id: "AT4", label: "Deliverable Specifications" },
  { id: "AT5", label: "Brand Logo Usage" },
  { id: "AT6", label: "SKU/Product Variant" },
  { id: "AT7", label: "Legal Statements Accuracy" },
  { id: "AT8", label: "Claims Alignment" },
  { id: "AT9", label: "Layout vs Brand Guidelines" },
  { id: "AT10", label: "Retailer/Channel Requirements" },
];

// Task category filter options (sorted alphabetically by label)
const TASK_CATEGORY_OPTIONS = ALL_TASK_CATEGORIES.map((cat) => ({
  value: cat.id,
  label: cat.label,
})).sort((a, b) => a.label.localeCompare(b.label));

// Date range preset options (matching ROI Dashboard)
type DatePreset =
  | "all_time"
  | "this_week"
  | "this_month"
  | "this_quarter"
  | "year_to_date"
  | "custom";

const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  all_time: "All Time",
  this_week: "This Week",
  this_month: "This Month",
  this_quarter: "This Quarter",
  year_to_date: "Year to Date",
  custom: "Custom Range...",
};

function getDateRangeFromPreset(preset: DatePreset): {
  startDate: string | undefined;
  endDate: string | undefined;
} {
  const now = new Date();
  const today = now.toISOString().split("T")[0];

  switch (preset) {
    case "all_time":
      return { startDate: undefined, endDate: undefined };
    case "this_week": {
      const dayOfWeek = now.getDay();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - dayOfWeek);
      return {
        startDate: startOfWeek.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "this_month": {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: startOfMonth.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "this_quarter": {
      const quarter = Math.floor(now.getMonth() / 3);
      const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
      return {
        startDate: startOfQuarter.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "year_to_date": {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return {
        startDate: startOfYear.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "custom":
      return { startDate: undefined, endDate: undefined };
    default:
      return { startDate: undefined, endDate: undefined };
  }
}

// Determine preset from URL date range
function getPresetFromDateRange(
  startDate: string | undefined,
  endDate: string | undefined,
): DatePreset {
  if (!startDate && !endDate) {
    return "all_time";
  }

  // Check if dates match any preset
  const presets: DatePreset[] = [
    "this_week",
    "this_month",
    "this_quarter",
    "year_to_date",
  ];

  for (const preset of presets) {
    const range = getDateRangeFromPreset(preset);
    if (range.startDate === startDate && range.endDate === endDate) {
      return preset;
    }
  }

  // If no preset matches, it's a custom range
  return "custom";
}

// Filter mode options
const FILTER_MODE_OPTIONS = [
  { value: "includes", label: "Includes" },
  { value: "has_issues", label: "Has Issues" },
];

// Helper function to format cycle date
function formatCycleDate(dateString: string | null): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Helper to get category label from ID
function getCategoryLabel(categoryId: string): string {
  const category = ALL_TASK_CATEGORIES.find((c) => c.id === categoryId);
  return category?.label || categoryId;
}

export default function AuditLogView() {
  const { t } = useTranslation("ui");
  const { atomicTasks, startDate, endDate, page, atomicTaskFilterMode } =
    auditLogRoute.useSearch();
  const navigate = auditLogRoute.useNavigate();

  useEffect(() => {
    document.title = "Audit Log | Puntt";
    return () => {
      document.title = "Puntt - Manual reviews. Automated for marketing teams.";
    };
  }, []);

  // atomicTasks is now a simple string to avoid URL encoding issues
  const selectedTaskCategory = atomicTasks;

  // Determine initial preset from URL params
  const initialPreset = getPresetFromDateRange(startDate, endDate);

  // Date filter state (matching ROI Dashboard)
  const [selectedPreset, setSelectedPreset] =
    useState<DatePreset>(initialPreset);
  const [showCustomRange, setShowCustomRange] = useState(
    initialPreset === "custom",
  );
  const [customStartDate, setCustomStartDate] = useState(startDate ?? "");
  const [customEndDate, setCustomEndDate] = useState(endDate ?? "");

  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Sync state when URL params change (e.g., navigating from ROI dashboard)
  useEffect(() => {
    const preset = getPresetFromDateRange(startDate, endDate);
    setSelectedPreset(preset);
    setShowCustomRange(preset === "custom");
    setCustomStartDate(startDate ?? "");
    setCustomEndDate(endDate ?? "");
  }, [startDate, endDate]);

  // Fetch grouped audit log data
  const {
    data: auditLogData,
    isLoading,
    isError,
    error,
  } = useGroupedAuditLog({
    page: page ?? 1,
    pageSize: 50,
    atomicTasks: atomicTasks ? [atomicTasks] : undefined,
    startDate: startDate,
    endDate: endDate,
  });

  const handleTaskCategoryChange = (value: string) => {
    const filterValue = value === "__clear__" ? undefined : value;
    navigate({
      search: (prev) => ({
        ...prev,
        atomicTasks: filterValue, // Pass single string directly instead of array
        page: 1,
      }),
      replace: true,
    });
  };

  const handleFilterModeChange = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        atomicTaskFilterMode: value as "includes" | "has_issues",
        page: 1,
      }),
      replace: true,
    });
  };

  const handlePresetSelect = (preset: DatePreset) => {
    setSelectedPreset(preset);

    if (preset === "custom") {
      setShowCustomRange(true);
      return;
    }

    setShowCustomRange(false);
    const { startDate: newStart, endDate: newEnd } =
      getDateRangeFromPreset(preset);

    navigate({
      search: (prev) => ({
        ...prev,
        startDate: newStart,
        endDate: newEnd,
        page: 1,
      }),
      replace: true,
    });
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      navigate({
        search: (prev) => ({
          ...prev,
          startDate: customStartDate,
          endDate: customEndDate,
          page: 1,
        }),
        replace: true,
      });
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomRange(false);
    setSelectedPreset("all_time");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Apply client-side filtering for "has_issues" mode
  const filteredEntries = useMemo(() => {
    if (!auditLogData?.entries) return [];

    if (selectedTaskCategory && atomicTaskFilterMode === "has_issues") {
      return auditLogData.entries.filter((entry) =>
        entry.cycles.some((cycle) =>
          cycle.missedIssues.some(
            (issue) => issue.atomic_task === selectedTaskCategory,
          ),
        ),
      );
    }

    return auditLogData.entries;
  }, [auditLogData, selectedTaskCategory, atomicTaskFilterMode]);

  if (isLoading) {
    return (
      <article
        className="flex flex-col items-center justify-center gap-6 overflow-auto px-10 py-6"
        data-testid="roi-audit-log"
      >
        <Spinner size="3" />
        <Text size="3">{t("roi.view.loadingAuditLog")}</Text>
      </article>
    );
  }

  if (isError) {
    return (
      <article
        className="flex flex-col gap-6 overflow-auto px-10 py-6"
        data-testid="roi-audit-log"
      >
        <Heading as="h1" size="7" weight="bold">
          {t("roi.view.wrikeTasksAuditLog")}
        </Heading>
        <Text size="3" color="red">
          Error loading audit log:{" "}
          {(error as Error)?.message || t("roi.view.unknownError")}
        </Text>
      </article>
    );
  }

  // When has_issues client-side filter is active, use filtered count instead of server total
  const totalEntries =
    selectedTaskCategory && atomicTaskFilterMode === "has_issues"
      ? filteredEntries.length
      : (auditLogData?.total ?? 0);

  return (
    <article
      className="flex flex-col gap-6 overflow-auto px-10 py-6"
      data-testid="roi-audit-log"
    >
      {/* Header with Back Button */}
      <header>
        <Flex direction="column" gap="2">
          <Link to={roiRoute.to}>
            <Button variant="ghost" size="1" color="gray">
              <ArrowLeft size={16} />
              Back
            </Button>
          </Link>
          <Heading as="h1" size="7" weight="bold">
            {t("roi.view.wrikeTasksAuditLog")}
          </Heading>
        </Flex>
      </header>

      {/* Filters Row */}
      <Flex align="center" gap="3" wrap="wrap" data-testid="audit-log-filter">
        {/* Date Range Filter Dropdown (matching ROI Dashboard) */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <Button variant="soft" color="gray">
              {DATE_PRESET_LABELS[selectedPreset]}
              <CaretDown size={14} />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item onSelect={() => handlePresetSelect("all_time")}>
              {t("roi.view.allTime")}
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => handlePresetSelect("this_week")}>
              {t("roi.view.week")}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => handlePresetSelect("this_month")}
            >
              {t("roi.view.month")}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => handlePresetSelect("this_quarter")}
            >
              {t("roi.view.quarter")}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => handlePresetSelect("year_to_date")}
            >
              {t("roi.view.yearDate")}
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onSelect={() => handlePresetSelect("custom")}>
              {t("roi.view.customRange")}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {/* Custom Date Range Inputs */}
        {showCustomRange && (
          <Flex align="center" gap="2">
            <TextField.Root
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              placeholder={t("roi.view.startDate")}
              className="w-[150px]"
            />
            <Text size="2" color="gray">
              to
            </Text>
            <TextField.Root
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              placeholder={t("roi.view.endDate")}
              className="w-[150px]"
            />
            <Button
              data-testid="view-button-423"
              variant="soft"
              size="2"
              onClick={handleApplyCustomRange}
            >
              {t("common:actions.apply")}
            </Button>
            <Button
              data-testid="view-button-426"
              variant="ghost"
              color="gray"
              size="2"
              onClick={handleCancelCustomRange}
            >
              {t("common:actions.cancel")}
            </Button>
          </Flex>
        )}

        {/* Active Task Category Filter Pill */}
        {selectedTaskCategory && (
          <>
            <Select.Root
              value={selectedTaskCategory}
              onValueChange={handleTaskCategoryChange}
            >
              <Select.Trigger
                variant="surface"
                className="min-w-[180px]"
                data-testid="task-category-filter"
              />
              <Select.Content position="popper" side="bottom" sideOffset={4}>
                <Select.Item value="__clear__">
                  {t("common:actions.clear")}
                </Select.Item>
                {TASK_CATEGORY_OPTIONS.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>

            {/* Filter Mode Selector */}
            <Select.Root
              value={atomicTaskFilterMode || "includes"}
              onValueChange={handleFilterModeChange}
            >
              <Select.Trigger
                variant="surface"
                className="min-w-[120px]"
                data-testid="filter-mode-select"
              />
              <Select.Content>
                {FILTER_MODE_OPTIONS.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </>
        )}

        {/* Task Category Filter Dropdown - only show if not already filtering by category */}
        {!selectedTaskCategory && (
          <DropdownMenu.Root
            open={filterMenuOpen}
            onOpenChange={setFilterMenuOpen}
          >
            <DropdownMenu.Trigger>
              <Button variant="outline" color="gray">
                <PlusCircle size={16} />
                Task Category
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {TASK_CATEGORY_OPTIONS.map((option) => (
                <DropdownMenu.Item
                  key={option.value}
                  onSelect={() => handleTaskCategoryChange(option.value)}
                >
                  {option.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}

        {/* Results count */}
        <Text size="2" color="gray" className="ml-auto">
          {totalEntries} {totalEntries === 1 ? "task" : "tasks"} found
        </Text>
      </Flex>

      {/* Audit Log Table */}
      <Table.Root variant="surface" data-testid="audit-log-table">
        <Table.Header>
          <Table.Row className="bg-puntt-neutral-gray-3">
            <Table.ColumnHeaderCell className="w-[40px] font-medium" />
            <Table.ColumnHeaderCell className="min-w-[200px] font-medium">
              {t("roi.view.wrikeTask")}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[120px] font-medium">
              {t("roi.view.firstCycleDate")}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[100px] font-medium">
              {t("roi.view.reviewCycles")}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[100px] font-medium">
              {t("roi.view.automatedReviewTasks")}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[100px] font-medium">
              {t("roi.view.lowRiskHumanCaughtIssues")}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="min-w-[100px] font-medium">
              {t("roi.view.highRiskHumanCaughtIssues")}
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {filteredEntries.map((entry) => (
            <CollapsibleWrikeTaskRow
              key={entry.taskId}
              entry={entry}
              isExpanded={expandedTasks.has(entry.taskId)}
              onToggle={() => toggleTaskExpansion(entry.taskId)}
              activeTaskCategory={selectedTaskCategory}
            />
          ))}
        </Table.Body>
      </Table.Root>

      {filteredEntries.length === 0 && (
        <Flex className="py-4 text-puntt-neutral-gray-9">
          <Text size="3">{t("roi.view.noAuditLogEntriesFound")}</Text>
        </Flex>
      )}
    </article>
  );
}

interface CollapsibleWrikeTaskRowProps {
  entry: GroupedAuditLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
  activeTaskCategory?: string;
}

function CollapsibleWrikeTaskRow({
  entry,
  isExpanded,
  onToggle,
  activeTaskCategory,
}: CollapsibleWrikeTaskRowProps) {
  const { t } = useTranslation("ui");
  return (
    <>
      {/* Collapsed row - aggregated view */}
      <Table.Row
        className={cx(
          "cursor-pointer border-b border-puntt-neutral-gray-6 hover:bg-puntt-neutral-gray-2",
          isExpanded && "bg-puntt-neutral-gray-2",
        )}
        onClick={onToggle}
      >
        {/* Expand/Collapse toggle */}
        <Table.Cell className="w-[40px] text-center">
          {isExpanded ? (
            <CaretDown size={16} className="text-puntt-neutral-gray-9" />
          ) : (
            <CaretRight size={16} className="text-puntt-neutral-gray-9" />
          )}
        </Table.Cell>

        {/* Wrike Task */}
        <Table.Cell>
          <RadixLink
            data-testid="view-radix-link-593"
            href={
              entry.wrikePermalink ||
              `https://www.wrike.com/open.htm?id=${entry.taskId}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-puntt-accent-9 underline decoration-puntt-accent-9"
            onClick={(e) => e.stopPropagation()}
          >
            {entry.taskName}
          </RadixLink>
        </Table.Cell>

        {/* First Cycle Date */}
        <Table.Cell>
          <Text size="2">{formatCycleDate(entry.firstCycleStart)}</Text>
        </Table.Cell>

        {/* Review Cycles */}
        <Table.Cell>
          <Text size="2">{entry.reviewCycleCount}</Text>
        </Table.Cell>

        {/* Automated Review Tasks */}
        <Table.Cell>
          <Text size="2">{entry.totalAtomicTasks}</Text>
        </Table.Cell>

        {/* Low Risk Human Caught Issues */}
        <Table.Cell>
          <Text
            size="2"
            color={entry.totalHumanIssues > 0 ? "yellow" : undefined}
          >
            {entry.totalHumanIssues}
          </Text>
        </Table.Cell>

        {/* High Risk Human Caught Issues */}
        <Table.Cell>
          <Text size="2">0</Text>
        </Table.Cell>
      </Table.Row>

      {/* Expanded rows - individual cycle details */}
      {isExpanded &&
        entry.cycles.map((cycle, index) => {
          // Use ticketTitles if available, otherwise fall back to ticketIds
          const ticketTitle = cycle.ticketTitles?.[0] || cycle.ticketIds[0];
          const ticketId = cycle.ticketIds[0];

          return (
            <Table.Row
              key={`${entry.taskId}-cycle-${index}`}
              className="border-b border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1"
            >
              {/* Empty cell for alignment */}
              <Table.Cell className="w-[40px]" />

              {/* Full width content area */}
              <Table.Cell colSpan={6}>
                <Flex direction="column" gap="3" className="py-2">
                  {/* Row 1: Ticket title on left, review date on right */}
                  <Flex justify="between" align="center">
                    <Flex align="center" gap="2">
                      {ticketId ? (
                        <RadixLink asChild>
                          <Link
                            to="/tickets/$ticketId/view"
                            params={{ ticketId }}
                            className="text-sm font-medium text-puntt-accent-9 underline decoration-puntt-accent-9"
                          >
                            {ticketTitle}
                          </Link>
                        </RadixLink>
                      ) : (
                        <Text size="2">—</Text>
                      )}
                    </Flex>
                    <Text size="2" color="gray">
                      {formatCycleDate(cycle.cycleStart)}
                    </Text>
                  </Flex>

                  {/* Row 2: Two-column layout - Checks on left, Issues on right */}
                  <Flex gap="4" className="w-full">
                    {/* Left column: Checks (only those without issues) */}
                    <Flex direction="column" gap="2" className="w-1/2">
                      <Text size="2" weight="medium" color="gray">
                        {t("roi.view.automatedReviewTasks")}
                      </Text>
                      <Flex wrap="wrap" gap="2">
                        {cycle.atomicTasks.map((taskId) => {
                          const isFiltered = activeTaskCategory === taskId;

                          return (
                            <span
                              key={taskId}
                              className={cx(
                                "rounded border border-puntt-neutral-gray-6 px-2 py-1 text-sm font-medium",
                                isFiltered && "bg-puntt-accent-3",
                              )}
                            >
                              {getCategoryLabel(taskId)}
                            </span>
                          );
                        })}
                      </Flex>
                    </Flex>

                    {/* Right column: Issues Found */}
                    <Flex direction="column" gap="2" className="w-1/2">
                      <Text size="2" weight="medium" color="gray">
                        {t("roi.view.humanCaughtIssues")}
                      </Text>
                      {cycle.missedIssues.length > 0 ? (
                        <Flex wrap="wrap" gap="2">
                          {cycle.atomicTasks
                            .filter((taskId) =>
                              cycle.missedIssues.some(
                                (i) => i.atomic_task === taskId,
                              ),
                            )
                            .map((taskId) => {
                              const issuesForTask = cycle.missedIssues.filter(
                                (i) => i.atomic_task === taskId,
                              );
                              const isFiltered = activeTaskCategory === taskId;

                              return (
                                <Flex
                                  key={taskId}
                                  direction="column"
                                  gap="1"
                                  className={cx(
                                    "border-yellow-300 bg-yellow-50 rounded border p-2",
                                    isFiltered && "bg-puntt-accent-3",
                                  )}
                                >
                                  <Text
                                    size="2"
                                    weight="medium"
                                    className="text-yellow-700"
                                  >
                                    {getCategoryLabel(taskId)}
                                  </Text>
                                  <ul className="m-0 list-inside list-disc pl-0">
                                    {issuesForTask.map((issue, idx) => (
                                      <li key={idx} className="text-xs">
                                        {issue.comment_text}
                                      </li>
                                    ))}
                                  </ul>
                                </Flex>
                              );
                            })}
                        </Flex>
                      ) : (
                        <Text size="2" color="gray" className="italic">
                          {t("roi.view.noIssuesFound")}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Table.Cell>
            </Table.Row>
          );
        })}
    </>
  );
}
